import type { CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import type { PublisherListOut } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import type { FilterOption } from '@/components/common/SelectFilter';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type PublisherSelectFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
  /**
   * Narrow the options: `parents` (`parent_id IS NULL`) or `imprints`
   * (`parent_id IS NOT NULL`). Omitted — every publisher.
   */
  only?: 'parents' | 'imprints';
};

const ONLY_FILTERS: Record<
  NonNullable<PublisherSelectFilterProps['only']>,
  CrudFilter[]
> = {
  parents: [{ field: 'parent_id', operator: 'null', value: true }],
  imprints: [{ field: 'parent_id', operator: 'nnull', value: true }],
};

/**
 * `publisher_id[eq]` column filter with server-side name autocomplete —
 * type a name fragment, pick a publisher, filter by its id. With `only` the
 * options (search included) are limited to parents or imprints.
 */
export const PublisherSelectFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
  only,
}: PublisherSelectFilterProps) => {
  const current = getFilterValue<number>(filters, field, 'eq');

  const { selectProps } = useSelect<PublisherListOut>({
    resource: 'user_catalog.publishers',
    optionLabel: 'name_with_role',
    optionValue: 'id',
    // Permanent filter — useSelect appends the search filters to it.
    filters: only ? ONLY_FILTERS[only] : [],
    // Hydrates the option label when the filter is already set (e.g. from URL).
    defaultValue: current,
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  return (
    <SelectFilter<number>
      field={field}
      options={(selectProps.options ?? []) as FilterOption<number>[]}
      filters={filters}
      confirm={confirm}
      clearFilters={clearFilters}
      setFilters={setFilters}
      placeholder={placeholder}
      minWidth={260}
      selectProps={{
        showSearch: true,
        filterOption: false,
        onSearch: selectProps.onSearch,
      }}
    />
  );
};
